import axiosInstance from '../../api';
import API_ENDPOINTS from '../../api/endpoints';
import useGeneralStore from '../../../Store/generalStore';
import { parseApiResponse, extractData, isSuccessResponse, getErrorMessage } from '../../api/responseModel';
import { notifyApiError } from '../../api/errorUtils';

const useTour = () => {
    const { setLoading, setMessage, setError } = useGeneralStore();

    const getFeatured = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(API_ENDPOINTS.TOUR.GET_FEATURED);
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                return extractData(apiResponse) || [];
            }

            const errorMsg = getErrorMessage(apiResponse) || 'Failed to fetch featured tours.';
            setError(errorMsg);
            return [];
        } catch (error) {
            notifyApiError(setError, error, 'Failed to fetch featured tours.');
            return [];
        } finally {
            setLoading(false);
        }
    };

    const getDestinations = async () => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.TOUR.DESTINATIONS);
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                return extractData(apiResponse) || [];
            }

            return [];
        } catch (error) {
            console.error('Failed to fetch destinations:', error);
            return [];
        }
    };

    /**
     * @param {Object} searchParams
     * @returns {Promise<{ tours: Array, pageNumber: number, pageSize: number, totalCount: number }>}
     */
    const search = async (searchParams = {}) => {
        try {
            setLoading(true);

            const requestBody = {
                destination: searchParams.destination ?? null,
                productTypeId: searchParams.productTypeId ?? null,
                categoryId: searchParams.categoryId ?? null,
                keyword: searchParams.keyword ?? null,
                fromDate: searchParams.fromDate ?? null,
                toDate: searchParams.toDate ?? null,
                pageNumber: searchParams.pageNumber || 1,
                pageSize: searchParams.pageSize || 12,
            };

            const response = await axiosInstance.post(API_ENDPOINTS.TOUR.SEARCH, requestBody);
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                const data = extractData(apiResponse);
                const tours = data?.data ?? data?.Data ?? [];
                return {
                    tours: Array.isArray(tours) ? tours : [],
                    pageNumber: data?.pageNumber ?? data?.PageNumber ?? requestBody.pageNumber,
                    pageSize: data?.pageSize ?? data?.PageSize ?? requestBody.pageSize,
                    totalCount: data?.totalCount ?? data?.TotalCount ?? 0,
                };
            }

            const errorMsg = getErrorMessage(apiResponse) || 'No tours found.';
            setError(errorMsg);
            return { tours: [], pageNumber: 1, pageSize: 12, totalCount: 0 };
        } catch (error) {
            notifyApiError(setError, error, 'Failed to search tours.');
            return { tours: [], pageNumber: 1, pageSize: 12, totalCount: 0 };
        } finally {
            setLoading(false);
        }
    };

    const getById = async (id) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(API_ENDPOINTS.TOUR.GET_BY_ID, {
                params: { id },
            });
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                return extractData(apiResponse);
            }

            const errorMsg = getErrorMessage(apiResponse) || 'Tour not found.';
            setError(errorMsg);
            return null;
        } catch (error) {
            notifyApiError(setError, error, 'Failed to load tour details.');
            return null;
        } finally {
            setLoading(false);
        }
    };

    const getAvailability = async (tourProductOptionId, fromDate, toDate) => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.TOUR.AVAILABILITY, {
                params: { tourProductOptionId, fromDate, toDate },
            });
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                return extractData(apiResponse) || [];
            }

            return [];
        } catch (error) {
            console.error('Failed to fetch availability:', error);
            return [];
        }
    };

    const getCategories = async () => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.TOUR.CATEGORIES);
            const apiResponse = parseApiResponse(response);

            if (isSuccessResponse(apiResponse)) {
                return extractData(apiResponse) || [];
            }

            return [];
        } catch (error) {
            console.error('Failed to fetch categories:', error);
            return [];
        }
    };

    return {
        getFeatured,
        getDestinations,
        search,
        getById,
        getAvailability,
        getCategories,
        setMessage,
    };
};

export default useTour;
